import { StateCreator } from "zustand";

import Bugsnag from "@bugsnag/js";
import { UsersAPI } from "api/users";
import { clearTabToken, setTabToken } from "common/MetaActions";
import { computePermissions } from "common/permissions/computePermissions";
import { failedLoginTeams, isInTeams, successLoginTeams } from "common/teams";
import { jwtDecode } from "jwt-decode";
import { TokenDecoded } from "models/login/login";
import { AppPermissions, DEFAULT_PERMISSIONS } from "models/permissions/permissions";
import { SysMessage, UserInfo } from "models/user/user";
import { RootState } from "store";

type FetchUserLoginProps = {
  code: string;
  password: string;
  username: string;
};

export interface UserSlice {
  isNewPass: boolean;
  isAuthenticating: boolean;
  isAuthenticated: boolean;
  token?: string;
  userInfo?: UserInfo;
  permissions: AppPermissions;
  loginFailed: boolean;
  loginTrue: boolean;
  showNotActivatedMessage: boolean;
  sysMessage: SysMessage;
  loginTimeout?: any;
  sessionExpired: boolean;
  noMatchingEntraUserInformation?: {
    tenantId: string;
    objectId: string;
  } | null;

  setSessionExpired: (val: boolean) => void;
  loggingIn: () => void;
  loginUser: (token: string | null) => void;
  loginError: () => void;
  noMatchingEntraUserError: (tenantId: string, objectId: string) => void;
  loginErrorNotActivated: () => void;
  fetchInternalUserLogin: (code: string | null) => void;
  fetchUserLogin: ({ code, password, username }: FetchUserLoginProps) => void;
  validateToken: (token: string) => void;
  setPasswordAndGetAuthFromKey: (key: string, password: string) => void;
  fetchInternalUserLoginEntraAccessToken: (accessToken: string) => void;
  fetchExternalUserLoginEntraAccessToken: (accessToken: string) => void;
  reset: () => void;
}

export const createUserSlice: StateCreator<
  RootState,
  [["zustand/devtools", never]],
  [],
  UserSlice
> = (set, get) => ({
  isAuthenticating: false,
  isAuthenticated: false,
  token: undefined,
  userInfo: undefined,
  permissions: DEFAULT_PERMISSIONS,
  loginFailed: false,
  loginTrue: false,
  showNotActivatedMessage: false,
  sessionExpired: false,
  sysMessage: {
    message: "",
    validUntil: undefined
  },
  isNewPass: false,

  reset: () =>
    set(
      ({ user }) => ({
        user: {
          ...user,
          isAuthenticating: false,
          loginFailed: false,
          isAuthenticated: false,
          userInfo: undefined,
          permissions: DEFAULT_PERMISSIONS,
          token: undefined,
          showNotActivatedMessage: false,
          sessionExpired: false,
          noMatchingEntraUserInformation: undefined,
        }
      }),
      false,
      "user/RESET"
    ),

  loggingIn: () =>
    set(
      ({ user }) => ({
        user: {
          ...user,
          loginTimeout: false,
          loginFailed: false,
          isAuthenticating: true,
          noMatchingEntraUserInformation: undefined,
        }
      }),
      false,
      "user/LOGGING_IN"
    ),

  loginUser: (token) => {
    if (token === null) {
      return;
    }

    const tokenDecoded = jwtDecode<TokenDecoded>(token);
    const userInfo: UserInfo = {
      ...tokenDecoded,
      type: tokenDecoded.id.startsWith("External") ? "External" : "Internal"
    };
    set(
      ({ user }) => ({
        user: {
          ...user,
          loginFailed: false,
          loginTimeout: false,
          isAuthenticated: !!token,
          isAuthenticating: false,
          sessionExpired: false,
          token,
          userInfo,
          permissions: computePermissions(userInfo, tokenDecoded),
        }
      }),
      false,
      "user/LOGIN_USER"
    );
  },

  loginError: () =>
    set(
      ({ user }) => ({
        user: {
          ...user,
          loginFailed: true,
          isAuthenticated: false,
          isAuthenticating: false,
          userInfo: undefined,
          token: ""
        }
      }),
      false,
      "user/LOGIN_ERROR"
    ),

  noMatchingEntraUserError: (tenantId: string, objectId: string) =>
    set(
      ({ user }) => ({
        user: {
          ...user,
          loginFailed: false,
          isAuthenticated: false,
          isAuthenticating: false,
          userInfo: undefined,
          token: "",
          noMatchingEntraUserInformation: { tenantId, objectId },
        }
      }),
      false,
      "user/NO_MATCHING_ENTRA_USER_ERROR"
    ),

  loginErrorNotActivated: () =>
    set(
      ({ user }) => ({
        user: {
          ...user,
          loginFailed: true,
          showNotActivatedMessage: true,
          isAuthenticated: false,
          isAuthenticating: false,
          userInfo: undefined,
          token: ""
        }
      }),
      false,
      "user/LOGIN_ERROR_NOT_ACTIVATED"
    ),

  setSessionExpired: (val: boolean) =>
    set(
      ({ user }) => ({
        user: {
          ...user,
          sessionExpired: val
        }
      }),
      false,
      "user/SESSION_EXPIRED"
    ),

  fetchInternalUserLogin: async (code) => {
    set({}, false, "user/FETCH_INTERNAL_USER_LOGIN");
    get().user.loggingIn();
    try {
      console.log("Fetching internal user login with code:", code);
      const response = await UsersAPI.fetchInternalUserLogin(code);
      let loginUrl, token;

      try {
        loginUrl = response.loginUrl;
        token = response.token;
      } catch (error) {
        token = response;
      }

      if (loginUrl) {
        if (process.env.isLocal) {
          const redirectUri =
            window.location.origin +
            "/" +
            loginUrl
              .match(/(redirect_uri=)[^&]+/)[0]
              .split("=")[1]
              .split("/")
              .slice(3)
              .join("/");
          loginUrl = loginUrl.replace(
            /(redirect_uri=)[^&]+/,
            "$1" + redirectUri
          );
        }
        window.location.href = loginUrl;
        return;
      }

      if (isInTeams()) {
        return successLoginTeams(token);
      }
      setTabToken(token);
      get().user.loginUser(token);
    } catch (error) {
      // Error already handled by errorHandlingPlugin
      if (isInTeams()) {
        failedLoginTeams("Hämtning av intern användarinloggning misslyckades.");
      }
      let statusCode: number | undefined;

      if (error && typeof error === "object" && "status" in error) {
        statusCode = (error as { status: number }).status;
      }
      if (statusCode === 403) {
        get().user.loginErrorNotActivated();
      } else {
        get().user.loginError();
      }
      Bugsnag.notify(error as any);
    }
  },

  fetchInternalUserLoginEntraAccessToken: async (accessToken) => {
    set({}, false, "user/FETCH_INTERNAL_USER_LOGIN_ENTRA_ACCESS_TOKEN");
    get().user.loggingIn();
    const response = await UsersAPI.fetchInternalUserLoginEntraAccessToken(accessToken);
    const token = response.token;
    setTabToken(token);
    get().user.loginUser(token);
  },

  fetchExternalUserLoginEntraAccessToken: async (accessToken) => {
    set({}, false, "user/FETCH_EXTERNAL_USER_LOGIN_ENTRA_ACCESS_TOKEN");
    get().user.loggingIn();
    try {
      const response = await UsersAPI.fetchExternalUserLoginEntraAccessToken(accessToken);
      const token = response.token;
      setTabToken(token);
      get().user.loginUser(token);
    } catch (error) {
      if (error && typeof error === "object" && "status" in error) {
        const statusCode = (error as { status: number }).status;
        if (statusCode === 401) {
          // Extract user info from access token for redirect
          try {
        const tokenDecoded = jwtDecode<any>(accessToken);
        const tenantId = tokenDecoded.tid;
        const objectId = tokenDecoded.oid;
        get().user.noMatchingEntraUserError(tenantId, objectId);
        return;
          } catch {
            throw error; // rethrow original error if decoding fails
          }
        }
      }

      get().error.appendErrorNotification(
        "Hämtning av extern användarinloggning misslyckades.",
        error
      );
      get().user.loginError();
      throw error;
    }
  },

  fetchUserLogin: async ({ code, password, username }) => {
    set({}, false, "user/FETCH_USER_LOGIN");
    get().user.loggingIn();
    try {
      const response = await UsersAPI.fetchUserLogin({
        code,
        password,
        username
      });
      let loginUrl, token;

      try {
        loginUrl = response.loginUrl;
        token = response.token;
        if (!token) {
          throw new Error("INVALID_TOKEN");
        }
      } catch (error) {
        const err = error as any;
        if (err.message === "INVALID_TOKEN") {
          clearTabToken();
          // Error notification intentionally shown here for invalid token
          get().error.appendErrorNotification(
            "Hämtning av användarinloggning misslyckades.",
            error
          );

          setTimeout(() => {
            window.location.href = "/";
          }, 1500);

          return;
        }
        token = response.text;
      }

      if (loginUrl) {
        if (process.env.isLocal) {
          const redirectUri =
            window.location.origin +
            "/" +
            loginUrl
              .match(/(redirect_uri=)[^&]+/)[0]
              .split("=")[1]
              .split("/")
              .slice(3)
              .join("/");
          loginUrl = loginUrl.replace(
            /(redirect_uri=)[^&]+/,
            "$1" + redirectUri
          );
        }
        window.location.href = loginUrl;
        return;
      }

      if (isInTeams()) {
        return successLoginTeams(token);
      }

      setTabToken(token);
      get().user.loginUser(token);
    } catch (error) {
      // Error already handled by errorHandlingPlugin

      if (isInTeams()) {
        failedLoginTeams("Hämtning av användarinloggning misslyckades.");
      }

      let statusCode: number | undefined;

      if (error && typeof error === "object" && "status" in error) {
        statusCode = (error as { status: number }).status;
      }
      if (statusCode === 403) {
        get().user.loginErrorNotActivated();
      } else {
        get().user.loginError();
      }
      Bugsnag.notify(error as any);
    }
  },

  validateToken: async (token) => {
    set({}, false, "user/VALIDATE_TOKEN");
    try {
      const response = await UsersAPI.validateToken(token);
      setTabToken(response);
      get().user.loginUser(response);
    } catch (error) {
      // Error already handled by errorHandlingPlugin
    }
  },

  setPasswordAndGetAuthFromKey: async (key, password) => {
    set({}, false, "user/SET_PASSWORD_AND_GET_AUTH_FROM_KEY");
    try {
      const response = await UsersAPI.setPasswordAndGetAuthFromKey(
        key,
        password
      );
      const tokenWithoutQuotes = response.replace(/^"|"$/g, "");
      setTabToken(tokenWithoutQuotes);
      get().user.loginUser(tokenWithoutQuotes);
    } catch (error) {
      // Error already handled by errorHandlingPlugin
    }
  }
});
